/**
 * Minimal QR code encoder (byte mode, error correction level M, versions 1–10 → up to 213 bytes).
 * Enough for authenticator setup links (otpauth://…). Drawn locally so the secret never goes to a QR web service.
 * Returns a square grid of booleans (true = dark), without the quiet zone.
 */

// Level M: [ecc codewords per block, [blocks, data codewords per block], …]
const M_BLOCKS: [number, [number, number][]][] = [
  [10, [[1, 16]]],
  [16, [[1, 28]]],
  [26, [[1, 44]]],
  [18, [[2, 32]]],
  [24, [[2, 43]]],
  [16, [[4, 27]]],
  [18, [[4, 31]]],
  [22, [[2, 38], [2, 39]]],
  [22, [[3, 36], [2, 37]]],
  [26, [[4, 43], [1, 44]]],
];
const ALIGN: number[][] = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50]];

// GF(256) with polynomial 0x11D.
const EXP = new Array<number>(512);
const LOG = new Array<number>(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
}
const gmul = (a: number, b: number) => (a && b ? EXP[LOG[a]! + LOG[b]!]! : 0);

function rsGenerator(degree: number) {
  let g = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      next[j] = next[j]! ^ g[j]!;
      next[j + 1] = next[j + 1]! ^ gmul(g[j]!, EXP[i]!);
    }
    g = next;
  }
  return g;
}
function rsRemainder(data: number[], degree: number) {
  const gen = rsGenerator(degree);
  const rem = new Array<number>(degree).fill(0);
  for (const b of data) {
    const f = b ^ rem.shift()!;
    rem.push(0);
    for (let i = 0; i < degree; i++) rem[i] = rem[i]! ^ gmul(gen[i + 1]!, f);
  }
  return rem;
}

function bch(value: number, poly: number, polyBits: number, dataBits: number) {
  let v = value << (polyBits - 1);
  for (let i = dataBits + polyBits - 2; i >= polyBits - 1; i--) if ((v >> i) & 1) v ^= poly << (i - (polyBits - 1));
  return (value << (polyBits - 1)) | v;
}

export function qrMatrix(text: string): boolean[][] {
  const bytes = [...new TextEncoder().encode(text)];
  // Pick the smallest version that fits.
  let ver = 0;
  for (let v = 1; v <= 10; v++) {
    const cap = M_BLOCKS[v - 1]![1].reduce((s, [n, d]) => s + n * d, 0);
    const bits = 4 + (v < 10 ? 8 : 16) + 8 * bytes.length;
    if (bits <= cap * 8) {
      ver = v;
      break;
    }
  }
  if (!ver) throw new Error('Text too long for a QR code');
  const [eccLen, groups] = M_BLOCKS[ver - 1]!;
  const dataCap = groups.reduce((s, [n, d]) => s + n * d, 0);

  // Data bits: mode 0100, length, bytes, terminator, pad.
  const bits: number[] = [];
  const put = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1);
  };
  put(4, 4);
  put(bytes.length, ver < 10 ? 8 : 16);
  for (const b of bytes) put(b, 8);
  put(0, Math.min(4, dataCap * 8 - bits.length));
  while (bits.length % 8) bits.push(0);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) data.push(bits.slice(i, i + 8).reduce((a, b) => (a << 1) | b, 0));
  for (let pad = 0xec; data.length < dataCap; pad ^= 0xec ^ 0x11) data.push(pad);

  // Split into blocks, add error correction, interleave.
  const blocks: number[][] = [];
  let k = 0;
  for (const [n, d] of groups) for (let i = 0; i < n; i++) (blocks.push(data.slice(k, k + d)), (k += d));
  const eccs = blocks.map((b) => rsRemainder(b, eccLen));
  const codewords: number[] = [];
  const maxD = Math.max(...blocks.map((b) => b.length));
  for (let i = 0; i < maxD; i++) for (const b of blocks) if (i < b.length) codewords.push(b[i]!);
  for (let i = 0; i < eccLen; i++) for (const e of eccs) codewords.push(e[i]!);

  // Function patterns.
  const size = ver * 4 + 17;
  const grid: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const fixed: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const set = (x: number, y: number, dark: boolean) => {
    grid[y]![x] = dark;
    fixed[y]![x] = true;
  };
  for (const [cx, cy] of [[3, 3], [size - 4, 3], [3, size - 4]] as const) {
    for (let dy = -4; dy <= 4; dy++)
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx, y = cy + dy;
        if (x < 0 || y < 0 || x >= size || y >= size) continue;
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        set(x, y, d !== 2 && d !== 4);
      }
  }
  for (let i = 8; i < size - 8; i++) (set(6, i, i % 2 === 0), set(i, 6, i % 2 === 0));
  const al = ALIGN[ver - 1]!;
  for (const ay of al)
    for (const ax of al) {
      if ((ax === 6 && ay === 6) || (ax === 6 && ay === al[al.length - 1]) || (ay === 6 && ax === al[al.length - 1])) continue;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  // Reserve format areas (filled later) and the dark module.
  const drawFormat = (mask: number) => {
    const f = bch((0 << 3) | mask, 0x537, 11, 5) ^ 0x5412; // level M = 00
    const bit = (i: number) => ((f >> i) & 1) === 1;
    for (let i = 0; i <= 5; i++) set(8, i, bit(i));
    set(8, 7, bit(6));
    set(8, 8, bit(7));
    set(7, 8, bit(8));
    for (let i = 9; i < 15; i++) set(14 - i, 8, bit(i));
    for (let i = 0; i < 8; i++) set(size - 1 - i, 8, bit(i));
    for (let i = 8; i < 15; i++) set(8, size - 15 + i, bit(i));
    set(8, size - 8, true);
  };
  drawFormat(0);
  if (ver >= 7) {
    const v = bch(ver, 0x1f25, 13, 6);
    for (let i = 0; i < 18; i++) {
      const d = ((v >> i) & 1) === 1;
      const a = size - 11 + (i % 3), b = Math.floor(i / 3);
      set(a, b, d);
      set(b, a, d);
    }
  }

  // Place data in the zigzag.
  let bi = 0;
  const total = codewords.length * 8;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++)
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (fixed[y]![x]) continue;
        if (bi < total) grid[y]![x] = ((codewords[bi >> 3]! >> (7 - (bi & 7))) & 1) === 1;
        bi++;
      }
  }

  const MASKS: ((x: number, y: number) => boolean)[] = [
    (x, y) => (x + y) % 2 === 0,
    (_x, y) => y % 2 === 0,
    (x) => x % 3 === 0,
    (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
    (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
    (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
  ];
  const applyMask = (m: number) => {
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (!fixed[y]![x] && MASKS[m]!(x, y)) grid[y]![x] = !grid[y]![x];
  };
  const penalty = () => {
    let p = 0;
    const line = (get: (i: number, j: number) => boolean) => {
      for (let i = 0; i < size; i++) {
        let run = 1;
        for (let j = 1; j <= size; j++) {
          if (j < size && get(i, j) === get(i, j - 1)) run++;
          else {
            if (run >= 5) p += run - 2;
            run = 1;
          }
        }
        for (let j = 0; j + 10 < size + 1; j++) {
          const s = Array.from({ length: 11 }, (_, t) => (j + t < size ? get(i, j + t) : false));
          const pat = [true, false, true, true, true, false, true];
          const a = pat.every((v, t) => s[t] === v) && s.slice(7, 11).every((v) => !v);
          const b = s.slice(0, 4).every((v) => !v) && pat.every((v, t) => s[t + 4] === v);
          if (a || b) p += 40;
        }
      }
    };
    line((i, j) => grid[i]![j]!);
    line((i, j) => grid[j]![i]!);
    for (let y = 0; y < size - 1; y++)
      for (let x = 0; x < size - 1; x++) {
        const c = grid[y]![x];
        if (c === grid[y]![x + 1] && c === grid[y + 1]![x] && c === grid[y + 1]![x + 1]) p += 3;
      }
    const dark = grid.flat().filter(Boolean).length;
    p += Math.floor(Math.abs((dark * 20) / (size * size) - 10)) * 10;
    return p;
  };
  let best = 0, bestP = Infinity;
  for (let m = 0; m < 8; m++) {
    applyMask(m);
    drawFormat(m);
    const p = penalty();
    if (p < bestP) (bestP = p), (best = m);
    applyMask(m);
  }
  applyMask(best);
  drawFormat(best);
  return grid;
}

/** SVG markup for a QR code (with a 4-module white border). */
export function qrSvg(text: string, px = 220): string {
  const g = qrMatrix(text);
  const n = g.length + 8;
  let d = '';
  g.forEach((row, y) => row.forEach((on, x) => on && (d += `M${x + 4} ${y + 4}h1v1h-1z`)));
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${n} ${n}" width="${px}" height="${px}" shape-rendering="crispEdges"><rect width="${n}" height="${n}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}
